import type { CrawlJobPeriodCountDTO } from '@liks/lac-svc-api/esm/models/index';
import type { CostChartViewMode } from '../costs/costChartTypes';
import {
  formatCalendarMonth,
  normalizeCalendarDay,
  normalizeCalendarMonth,
  parseLocalCalendarDay,
  toCalendarDay,
} from '../../utils/dateTimeUtils';

export type CrawlJobTimeSeriesPoint = {
  label: string;
  count: number;
};

export type CrawlJobTimeSeriesStackedPoint = {
  label: string;
  total: number;
  [tenantName: string]: string | number;
};

export type StackedCrawlJobTimeSeriesChartData = {
  points: CrawlJobTimeSeriesStackedPoint[];
  tenants: string[];
};

function iterateMonths(fromMonth: string, toMonth: string): string[] {
  const [startYear, startMonthNum] = fromMonth.split('-').map(Number);
  const [endYear, endMonthNum] = toMonth.split('-').map(Number);
  const result: string[] = [];
  let year = startYear;
  let month = startMonthNum;

  while (year < endYear || (year === endYear && month <= endMonthNum)) {
    result.push(formatCalendarMonth(year, month));
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }

  return result;
}

function collectTenants(rows: CrawlJobPeriodCountDTO[]): string[] {
  const tenants = new Set<string>();
  for (const row of rows) {
    const tenantName = row.tenantName?.trim();
    if (tenantName) {
      tenants.add(tenantName);
    }
  }
  return Array.from(tenants).sort((left, right) => left.localeCompare(right));
}

function addStackedRow(
  map: Map<string, Map<string, number>>,
  periodStart: string,
  tenantName: string,
  crawlJobCount: number | null | undefined,
): void {
  let periodMap = map.get(periodStart);
  if (!periodMap) {
    periodMap = new Map();
    map.set(periodStart, periodMap);
  }
  periodMap.set(
    tenantName,
    (periodMap.get(tenantName) ?? 0) + (crawlJobCount ?? 0),
  );
}

function buildStackedPoint(
  label: string,
  periodMap: Map<string, number> | undefined,
  tenants: string[],
): CrawlJobTimeSeriesStackedPoint {
  const point: CrawlJobTimeSeriesStackedPoint = {
    label,
    total: 0,
  };

  for (const tenant of tenants) {
    const count = periodMap?.get(tenant) ?? 0;
    point[tenant] = count;
    point.total += count;
  }

  return point;
}

function aggregateStackedByDay(
  rows: CrawlJobPeriodCountDTO[],
  fromDay: string,
  toDay: string,
  tenants: string[],
): CrawlJobTimeSeriesStackedPoint[] {
  const map = new Map<string, Map<string, number>>();
  for (const row of rows) {
    if (!row.periodStart) {
      continue;
    }
    const tenantName = row.tenantName?.trim();
    if (!tenantName) {
      continue;
    }
    addStackedRow(
      map,
      normalizeCalendarDay(row.periodStart),
      tenantName,
      row.crawlJobCount,
    );
  }

  const result: CrawlJobTimeSeriesStackedPoint[] = [];
  const cursor = parseLocalCalendarDay(fromDay);
  const end = parseLocalCalendarDay(toDay);

  while (cursor <= end) {
    const label = toCalendarDay(cursor);
    result.push(buildStackedPoint(label, map.get(label), tenants));
    cursor.setDate(cursor.getDate() + 1);
  }

  return result;
}

function aggregateStackedByMonth(
  rows: CrawlJobPeriodCountDTO[],
  fromMonth: string,
  toMonth: string,
  tenants: string[],
): CrawlJobTimeSeriesStackedPoint[] {
  const map = new Map<string, Map<string, number>>();
  for (const row of rows) {
    if (!row.periodStart) {
      continue;
    }
    const tenantName = row.tenantName?.trim();
    if (!tenantName) {
      continue;
    }
    addStackedRow(
      map,
      normalizeCalendarMonth(row.periodStart),
      tenantName,
      row.crawlJobCount,
    );
  }

  return iterateMonths(fromMonth, toMonth).map((label) =>
    buildStackedPoint(label, map.get(label), tenants),
  );
}

function aggregateByDay(
  rows: CrawlJobPeriodCountDTO[],
  fromDay: string,
  toDay: string,
): CrawlJobTimeSeriesPoint[] {
  const map = new Map<string, number>();
  for (const row of rows) {
    if (!row.periodStart) {
      continue;
    }
    const day = normalizeCalendarDay(row.periodStart);
    map.set(day, (map.get(day) ?? 0) + (row.crawlJobCount ?? 0));
  }

  const result: CrawlJobTimeSeriesPoint[] = [];
  const cursor = parseLocalCalendarDay(fromDay);
  const end = parseLocalCalendarDay(toDay);

  while (cursor <= end) {
    const label = toCalendarDay(cursor);
    result.push({ label, count: map.get(label) ?? 0 });
    cursor.setDate(cursor.getDate() + 1);
  }

  return result;
}

function aggregateByMonth(
  rows: CrawlJobPeriodCountDTO[],
  fromMonth: string,
  toMonth: string,
): CrawlJobTimeSeriesPoint[] {
  const map = new Map<string, number>();
  for (const row of rows) {
    if (!row.periodStart) {
      continue;
    }
    const month = normalizeCalendarMonth(row.periodStart);
    map.set(month, (map.get(month) ?? 0) + (row.crawlJobCount ?? 0));
  }

  return iterateMonths(fromMonth, toMonth).map((label) => ({
    label,
    count: map.get(label) ?? 0,
  }));
}

export function buildCrawlJobTimeSeriesChartData(
  rows: CrawlJobPeriodCountDTO[],
  mode: CostChartViewMode,
  fromDay: string,
  toDay: string,
  fromMonth: string,
  toMonth: string,
): CrawlJobTimeSeriesPoint[] {
  if (mode === 'daily') {
    return aggregateByDay(rows, fromDay, toDay);
  }
  return aggregateByMonth(rows, fromMonth, toMonth);
}

export function buildStackedCrawlJobTimeSeriesChartData(
  rows: CrawlJobPeriodCountDTO[],
  mode: CostChartViewMode,
  fromDay: string,
  toDay: string,
  fromMonth: string,
  toMonth: string,
): StackedCrawlJobTimeSeriesChartData {
  const tenants = collectTenants(rows);
  const points =
    mode === 'daily'
      ? aggregateStackedByDay(rows, fromDay, toDay, tenants)
      : aggregateStackedByMonth(rows, fromMonth, toMonth, tenants);

  return { points, tenants };
}

export function formatCrawlJobTimeSeriesXAxisLabel(
  value: string,
  mode: CostChartViewMode,
): string {
  if (mode === 'daily') {
    return String(parseInt(value.slice(8), 10));
  }
  const [year, month] = value.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1, 1);
  return date.toLocaleDateString(undefined, {
    month: 'short',
    year: '2-digit',
  });
}

export function resolveCrawlJobTimeSeriesTenantLabel(
  tenantName: string,
  tenantLabels: Record<string, string> | undefined,
): string {
  return tenantLabels?.[tenantName]?.trim() || tenantName;
}
